import React, {useMemo} from "react";
import {Cell, Pie, PieChart, ResponsiveContainer, Tooltip} from "recharts";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBan, faBell, faBolt, faCubes, faLock, faPersonRunning, faTag, faWifi} from "@fortawesome/free-solid-svg-icons";
import * as Constants from "../../utils/Constants";
import LabelMultiSelect from "../common/LabelMultiSelect";
// Shared staleness rule — single source of truth (see utils/deviceStatus.js).
import {isGt06Stale, showAlertBell} from "../../utils/deviceStatus";

const TYPE_META = {
  GPS_LOCK: {label: "GPS Lock", color: "#FF683A"},
  BT_LOCK: {label: "BT Lock", color: "#3b82f6"},
  MOBI_TRACK: {label: "Track Point", color: "#8b5cf6"},
  GT06_TRACKER: {label: "GPS Tracker", color: "#10b981"},
};

const isLocked = (v) => v === "Locked" || v === "LOCKED";
const isUnlocked = (v) => v === "Open" || v === "OPEN" || v === "Unlocked";
const isLockMoving = (v) => typeof v === "string" && v.toLowerCase().includes("moving");

// Unified online rule — GT06 must also have a fresh frame (matches DeviceList).
const isDeviceOnline = (d) => {
  if (d.deviceStatusF2 !== Constants.DEVICE_ONLINE) return false;
  if (d.assetType === "GT06_TRACKER") return !isGt06Stale(d.lastFrameUtc);
  return true;
};

// Movement is device-type aware: GPS locks report it as a string; GT06 trackers
// move when online with positive speed.
const isDeviceMoving = (d) => {
  if (d.assetType === "GT06_TRACKER") return isDeviceOnline(d) && Number(d.speedKmph) > 0;
  return isLockMoving(d.assetMovementStatusF5);
};
const hasMovementConcept = (d) =>
  d.assetType === "GT06_TRACKER" ||
  (d.assetMovementStatusF5 && d.assetMovementStatusF5 !== "N/A" && d.assetMovementStatusF5 !== Constants.NO_INFO);

/**
 * Full-width dashboard analytics band — derived entirely from the device data already on screen
 * (status, lock state, motion, ignition, battery, alerts). Point 12 (analytics in dashboard) +
 * point 2 (label filter).
 */
export default function DashboardInsights({devices, labels, selectedLabelIds, onToggleLabel, scopeLabel, compact = false}) {
  const stats = useMemo(() => {
    const total = devices.length;
    // Online — GT06 staleness-aware (matches the device list)
    const online = devices.filter(isDeviceOnline).length;
    // Count only alerts the user can actually open — Track Point duty alerts light the
    // count endpoint but have no viewable detail yet, so they're excluded (same rule as
    // the row bell in DeviceList via showAlertBell).
    const alerts = devices.filter(showAlertBell).length;
    const charging = devices.filter((d) => d.batteryStatusF3 === "Charging").length;

    // Lock state (locks only)
    const locked = devices.filter((d) => isLocked(d.lockStatusF4)).length;
    const unlocked = devices.filter((d) => isUnlocked(d.lockStatusF4)).length;

    // Motion — device-type aware (locks via status string, trackers via speed)
    const movementApplicable = devices.filter(hasMovementConcept);
    const moving = movementApplicable.filter(isDeviceMoving).length;
    const idle = movementApplicable.length - moving;

    // Ignition (GT06 only)
    const trackers = devices.filter((d) => d.assetType === "GT06_TRACKER");
    const ignitionOn = trackers.filter((d) => d.accOn === true && isDeviceOnline(d)).length;
    const ignitionOff = trackers.length - ignitionOn;

    const byType = {};
    devices.forEach((d) => {
      byType[d.assetType] = (byType[d.assetType] || 0) + 1;
    });

    return {
      total, online, offline: total - online, alerts, charging,
      locked, unlocked, moving, idle, ignitionOn, ignitionOff,
      hasTrackers: trackers.length > 0,
      byType,
    };
  }, [devices]);

  const onlineRate = stats.total ? Math.round((stats.online / stats.total) * 100) : 0;

  const donutData = useMemo(
    () =>
      Object.entries(stats.byType).map(([type, value]) => ({
        name: TYPE_META[type]?.label || type,
        value,
        color: TYPE_META[type]?.color || "#cbd5e1",
      })),
    [stats.byType]
  );

  // Always render the analytics section for every role (point: visible to all users).
  if (stats.total === 0) {
    return (
      <div className={`dash-band dash-band-empty${compact ? " dash-band-compact" : ""}`}>
        {scopeLabel && <div className="dash-scope">{scopeLabel}</div>}
        <div className="dash-empty-hint">No devices to show yet. Analytics will appear once devices are assigned.</div>
      </div>
    );
  }

  return (
    <div className={`dash-band${compact ? " dash-band-compact" : ""}`}>
      {scopeLabel && <div className="dash-scope">{scopeLabel}</div>}

      {/* KPI cards */}
      <div className="dash-kpis">
        <KpiCard icon={faCubes} label="Total Assets" value={stats.total} accent="#363166" tint="#eef0fb" />
        <KpiCard icon={faWifi} label="Online" value={stats.online} sub={`${onlineRate}%`} accent="#10b981" tint="#e7f8f0" />
        <KpiCard icon={faBan} label="Offline" value={stats.offline} accent="#94a3b8" tint="#f1f5f9" />
        <KpiCard icon={faBell} label="With Alerts" value={stats.alerts} accent="#ef4444" tint="#fdecec" />
        <KpiCard icon={faLock} label="Locked" value={stats.locked} accent="#FF683A" tint="#fff1e6" />
        <KpiCard icon={faPersonRunning} label="Moving" value={stats.moving} accent="#3b82f6" tint="#e8f0fe" />
      </div>

      {/* Distribution donut */}
      <div className="dash-donut-card">
        <div className="dash-card-title">Fleet by Device Type</div>
        <div className="dash-donut-wrap">
          <div className="dash-donut-chart">
            <ResponsiveContainer width="100%" height={130}>
              <PieChart>
                <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={38} outerRadius={58} paddingAngle={2} stroke="none">
                  {donutData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="dash-donut-center">
              <div className="dash-donut-total">{stats.total}</div>
              <div className="dash-donut-sub">assets</div>
            </div>
          </div>
          <div className="dash-donut-legend">
            {donutData.map((d) => (
              <span key={d.name} className="dash-legend-item">
                <span className="dash-legend-dot" style={{background: d.color}} />
                {d.name} <strong>{d.value}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Operational status — segmented bars from live device data */}
      <div className="dash-ops-card">
        <div className="dash-card-title">Operational Status</div>
        <SegmentBar
          icon={faLock}
          label="Lock state"
          segments={[
            {label: "Locked", value: stats.locked, color: "#10b981"},
            {label: "Unlocked", value: stats.unlocked, color: "#FF683A"},
          ]}
        />
        <SegmentBar
          icon={faPersonRunning}
          label="Movement"
          segments={[
            {label: "Moving", value: stats.moving, color: "#3b82f6"},
            {label: "Idle", value: stats.idle, color: "#cbd5e1"},
          ]}
        />
        {stats.hasTrackers && (
          <SegmentBar
            icon={faBolt}
            label="Ignition"
            segments={[
              {label: "On", value: stats.ignitionOn, color: "#f59e0b"},
              {label: "Off", value: stats.ignitionOff, color: "#cbd5e1"},
            ]}
          />
        )}
      </div>

      {/* Label filter (point 2) — searchable autocomplete for consistency */}
      {labels && labels.length > 0 && (
        <div className="dash-label-card">
          <span className="dash-label-card-title"><FontAwesomeIcon icon={faTag} /> Filter by label</span>
          <LabelMultiSelect labels={labels} selectedIds={selectedLabelIds} onToggle={onToggleLabel} />
        </div>
      )}
    </div>
  );
}

function KpiCard({icon, label, value, sub, accent, tint}) {
  return (
    <div className="dash-kpi">
      <div className="dash-kpi-icon" style={{background: tint, color: accent}}>
        <FontAwesomeIcon icon={icon} />
      </div>
      <div className="dash-kpi-body">
        <div className="dash-kpi-value">
          {value}
          {sub != null && <span className="dash-kpi-sub">{sub}</span>}
        </div>
        <div className="dash-kpi-label">{label}</div>
      </div>
    </div>
  );
}

function SegmentBar({icon, label, segments}) {
  const total = segments.reduce((s, x) => s + x.value, 0);
  return (
    <div className="dash-seg">
      <div className="dash-seg-head">
        <span className="dash-seg-label"><FontAwesomeIcon icon={icon} /> {label}</span>
        <span className="dash-seg-counts">
          {segments.map((s) => (
            <span key={s.label}>
              <span className="dash-seg-dot" style={{background: s.color}} />{s.label} {s.value}
            </span>
          ))}
        </span>
      </div>
      <div className="dash-seg-bar">
        {total === 0 ? (
          <div className="dash-seg-empty" />
        ) : (
          segments.map((s) => (
            <div
              key={s.label}
              className="dash-seg-fill"
              style={{width: `${(s.value / total) * 100}%`, background: s.color}}
              title={`${s.label}: ${s.value}`}
            />
          ))
        )}
      </div>
    </div>
  );
}
